import { EngagementStepType } from '@aiwyn/api';

/**
 * Inputs to the engagement wizard footer-button visibility decision. Kept as a flat bag of primitives so
 * the decision is a pure function (testable without standing up the container component + its NGXS store).
 */
export interface EngagementFooterButtonInput {
  /** Type of the step currently shown. */
  currentStepType: EngagementStepType;
  /** Whether the current step itself is read-only (drives Approve & Submit). */
  isReadonlyCurrentStep: boolean;
  isOnFirstStep: boolean;
  isOnLastStep: boolean;
  isOnLastIncompleteStep: boolean;
  /** On the last step AND that step is already COMPLETE. */
  isLastStepCompleted: boolean;
  /** Engagement-level read-only: `engagement.isReadonly || steps.every(isReadonly)`. */
  isEngagementReadonly: boolean;
  isUsingSecureLink: boolean;
  /**
   * FIRM-10915 / PP-3635: the PAYMENT step is showing a hard-failed sync prepayment that can be retried in
   * place. The backend marks the FAILED PAYMENT step (and the LETTER step) `isReadonly`, which flips
   * `isEngagementReadonly` true and would otherwise suppress the footer CTA — but the failed state is
   * explicitly retryable ("Try Again" / "you may try again…"), so the complete-engagement CTA must render.
   */
  isFailedPrepaymentRetry: boolean;
}

export interface EngagementFooterButtonVisibility {
  showPreviousStepButton: boolean;
  showApproveAndSubmitButton: boolean;
  showNextStepButton: boolean;
  showCompleteEngagementButton: boolean;
  showCloseEngagementButton: boolean;
}

/**
 * Decide which engagement wizard footer buttons to show.
 *
 * Behavior is unchanged when {@link EngagementFooterButtonInput.isFailedPrepaymentRetry} is `false`. When it
 * is `true` the complete-engagement CTA (relabeled "Try Again") is forced on and the close-engagement CTA is
 * suppressed, so a retryable failed prepayment exposes its retry submit control on re-entry even though the
 * read-only steps would otherwise hide it. This intentionally does NOT touch the genuinely read-only
 * COMPLETED / PROCESSING prepayment states (they derive to a non-`failed` state, so the flag is `false`).
 */
export function resolveEngagementFooterButtonVisibility(
  input: EngagementFooterButtonInput,
): EngagementFooterButtonVisibility {
  const {
    currentStepType,
    isReadonlyCurrentStep,
    isOnFirstStep,
    isOnLastStep,
    isOnLastIncompleteStep,
    isLastStepCompleted,
    isEngagementReadonly,
    isUsingSecureLink,
    isFailedPrepaymentRetry,
  } = input;

  const showPreviousStepButton = !isOnFirstStep;

  /**
   * This logic doesn't handle EL step being (1) readonly and (2) the final step (3) with non-readonly steps
   * before it. We'd show complete if this ever becomes a possible situation (right now, EL step is always first).
   */
  const showApproveAndSubmitButton = [
    EngagementStepType.LETTER,
    EngagementStepType.CONSENT,
    EngagementStepType.ADD_ON,
  ].includes(currentStepType) && !isReadonlyCurrentStep;

  // if engagement or step is readonly, check if on last step. Otherwise, check if on last incomplete.
  // A failed-prepayment retry never wants the plain "Next" button — it owns the complete-engagement CTA.
  const showNextStepButton = !showApproveAndSubmitButton && !isFailedPrepaymentRetry && (
    (isEngagementReadonly && !isOnLastStep) ||
    (!isEngagementReadonly && !isOnLastStep && !isOnLastIncompleteStep)
  );

  const showCompleteEngagementButton =
    isFailedPrepaymentRetry
    || !(showApproveAndSubmitButton || showNextStepButton || isEngagementReadonly || isLastStepCompleted);

  const showCloseEngagementButton =
    !isUsingSecureLink && isEngagementReadonly && isOnLastStep && !isFailedPrepaymentRetry;

  return {
    showPreviousStepButton,
    showApproveAndSubmitButton,
    showNextStepButton,
    showCompleteEngagementButton,
    showCloseEngagementButton,
  };
}
